import * as yup from 'yup';
import { isPossiblePhoneNumber, CountryCode } from 'libphonenumber-js';

const phoneRegex = /^\(\d{3}\)[\s-]?\d{3}[\s-]?\d{4}$/;
const containsNumberRegex = /\d/;

type phoneValidationSchemaParams = {
  required?: boolean;
  invalidMessage?: string;
  requiredMessage?: string;
};

type internationalPhoneValidationSchemaParams = {
  required?: boolean;
  country?: string;
};

// Legacy US phone validation (maintained for backward compatibility)
export const phoneValidationSchema = ({
  required = false,
  invalidMessage,
  requiredMessage,
}: phoneValidationSchemaParams = {}) => {
  const invalidNumberMessage = invalidMessage || 'Enter a valid phone number';
  const requiredNumberMessage = requiredMessage || 'Phone number is required';

  if (required) {
    return yup
      .string()
      .trim()
      .matches(phoneRegex, invalidNumberMessage)
      .required(requiredNumberMessage);
  }

  return yup
    .string()
    .trim()
    .test('is-valid-phone', invalidNumberMessage, value => {
      return (
        !value || !containsNumberRegex.test(value) || phoneRegex.test(value)
      );
    });
};

// International phone validation using libphonenumber-js
export const internationalPhoneValidationSchema = ({
  required = false,
  country,
}: internationalPhoneValidationSchemaParams = {}) => {
  const invalidNumberMessage = 'Enter a valid phone number';

  if (required) {
    return yup
      .string()
      .trim()
      .required('Phone number is required')
      .test('is-valid-international-phone', invalidNumberMessage, value => {
        if (!value) return false;

        try {
          return isPossiblePhoneNumber(value, country as CountryCode);
        } catch {
          return false;
        }
      });
  }

  return yup
    .string()
    .trim()
    .test('is-valid-international-phone', invalidNumberMessage, value => {
      if (!value || !containsNumberRegex.test(value)) return true;

      try {
        // We're using isPossiblePhoneNumber here instead of isValidPhoneNumber
        // because newer area codes, like 324 Northeast Florida implemented Feb 2024,
        // are not in libphonenumber-js database yet.
        return isPossiblePhoneNumber(value, country as CountryCode);
      } catch {
        return false;
      }
    });
};
